import React, { useEffect, useMemo, useState } from 'react';

import { useIsMounted } from '@shared/hooks/use-is-mounted';
import APUtilResources from '@shared/utils/resources/resources.utils';
import { APUtilIndexedDB, INDEXDB_TABLE_NAMES } from '@shared/utils/storage/indexdb.util';

import type { IAPImageSVGProps, SvgCacheData } from './types';

const APImageSVG: React.FC<IAPImageSVGProps<SvgCacheData[]>> = ({
  icon,
  cacheBase,
  cache = [],
  indexDbTableName = 'icons_mcute',
  extension = 'svg',
  color,
  size,
  fullWidth,
  aspectRatio,
  flip,
  rotate,
  style,
  ...restProps
}) => {
  const [svgHtml, setSvgHtml] = useState('');
  const isMounted = useIsMounted();

  useEffect(() => {
    // First, check in provided cache
    const findSVG = cache.find((item) => item.icon === icon)?.svg;
    if (findSVG) {
      if (isMounted()) {
        setSvgHtml(findSVG);
      }
      return;
    }

    // Then check in IndexedDB
    APUtilIndexedDB.getRowBy(INDEXDB_TABLE_NAMES[indexDbTableName], icon)
      .then((dbRow) => {
        const svgHtml = dbRow ? (dbRow as { svg: string }).svg : undefined;
        if (svgHtml) {
          if (isMounted()) {
            setSvgHtml(svgHtml);
          }
          // Update cache if provided
          if (cache) {
            cache.push({ icon, svg: svgHtml });
          }
        } else {
          // Fetch from network as fallback
          const iconSrc = APUtilResources.buildImageUrl(cacheBase, icon, extension);

          if (!iconSrc) {
            throw new Error(`Icon source URL could not be generated for icon: ${icon}`);
          }
          return fetch(iconSrc)
            .then((response) => {
              if (response.status !== 200) {
                throw new Error(`Failed to fetch SVG ${icon}`);
              }
              return response.text();
            })
            .then((data) => {
              const parser = new DOMParser();
              const svgDoc = parser.parseFromString(data, 'image/svg+xml');
              const svg = svgDoc.documentElement.outerHTML;
              if (isMounted()) {
                setSvgHtml(svg);
              }
              // Update cache if provided
              if (cache) {
                cache.push({ icon, svg });
              }
              // Save to IndexedDB
              APUtilIndexedDB.addRow(INDEXDB_TABLE_NAMES[indexDbTableName], 'icon', {
                icon: icon,
                svg: svg,
              });
            });
        }
      })
      .catch((err: unknown) => {
        console.error('IndexDB Error', err);
        // Fallback to network fetch
        const iconSrc = APUtilResources.buildImageUrl(cacheBase, icon, extension);

        if (!iconSrc) {
          throw new Error(`Icon source URL could not be generated for icon: ${icon}`);
        }
        fetch(iconSrc)
          .then((response) => {
            if (response.status !== 200) {
              throw new Error(`Failed to fetch SVG ${icon}`);
            }
            return response.text();
          })
          .then((data) => {
            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(data, 'image/svg+xml');
            const svg = svgDoc.documentElement.outerHTML;
            if (isMounted()) {
              setSvgHtml(svg);
            }
            // Update cache if provided
            if (cache) {
              cache.push({ icon, svg });
            }
          })
          .catch((error) => {
            if (isMounted()) {
              setSvgHtml(
                '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><g fill="none" fill-rule="nonzero"><path d="M24 0v24H0V0h24ZM12.593 23.258l-.011.002-.071.035-.02.004-.014-.004-.071-.035c-.01-.004-.019-.001-.024.005l-.004.01-.017.428.005.02.01.013.104.074.015.004.012-.004.104-.074.012-.016.004-.017-.017-.427c-.002-.01-.009-.017-.017-.018Zm.265-.113-.013.002-.185.093-.01.01-.003.011.018.43.005.012.008.007.201.093c.012.004.023 0 .029-.008l.004-.014-.034-.614c-.003-.012-.01-.02-.02-.022Zm-.715.002a.023.023 0 0 0-.027.006l-.006.014-.034.614c0 .012.007.02.017.024l.015-.002.201-.093.01-.008.004-.011.017-.43-.003-.012-.01-.01-.184-.092Z"></path><path fill="currentColor" d="M20 3a2 2 0 0 1 1.995 1.85L22 5v14a2 2 0 0 1-1.85 1.995L20 21H4a2 2 0 0 1-1.995-1.85L2 19V5a2 2 0 0 1 1.85-1.995L4 3h16ZM9.879 12.05l-5.657 5.657a1.002 1.002 0 0 1-.222.168V19h16v-1.125a1 1 0 0 1-.222-.168L16.95 14.88l-.707.707.207.207a1 1 0 0 1-1.415 1.414L9.88 12.05ZM20 5H4v10.1l4.995-4.994a1.25 1.25 0 0 1 1.666-.091l.101.09 4.066 4.067 1.238-1.238a1.25 1.25 0 0 1 1.666-.091l.102.091L20 15.101V5Zm-4.5 2a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z"></path></g></svg>'
              );
            }
            console.error('Fetch ERROR', error);
          });
      });
  }, [icon, cacheBase, cache, indexDbTableName, extension, isMounted]);

  const _aspectRatio = useMemo(() => {
    if (aspectRatio === undefined) {
      return '1 / 1';
    }
    return aspectRatio;
  }, [aspectRatio]);

  const _size = useMemo(() => {
    switch (size) {
      case 'ultraLarge':
        return 32;
      case 'large':
        return 24;
      case 'medium':
        return 20;
      case 'base':
        return 20;
      case 'small':
        return 18;
      case 'smaller':
        return 16;
      default:
        if (size === undefined) {
          return 17;
        }
        return size;
    }
  }, [size]);

  const transform = useMemo(() => {
    let result = '';
    if (flip !== undefined) {
      result += `scaleX(${flip === 'horizontal' || flip === 'both' ? -1 : 1}) scaleY(${
        flip === 'vertical' || flip === 'both' ? -1 : 1
      })`;
    }
    if (rotate !== undefined) {
      result += ` rotate(${rotate}deg) `;
    }
    return result;
  }, [flip, rotate]);

  return (
    <div
      style={{
        ...style,
        display: 'flex',
        width: fullWidth ? '100%' : _size,
        minWidth: fullWidth ? '100%' : _size,
        aspectRatio: _aspectRatio,
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        transform: transform,
        color: color,
        fill: color,
      }}
      {...restProps}
      dangerouslySetInnerHTML={{ __html: svgHtml }}
    ></div>
  );
};

export default APImageSVG;
